import { BodyText, Button, Flex_unstable as Flex, Heading, Separator } from '@farol-ds/react'
import { useOnScreen } from '@jusbrasil-web/shared-hooks'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useMemo, useRef } from 'react'

import styles from './variant-b.module.scss'

const DEFAULT_EXPLORE_BUTTON_LABEL = 'Buscar em todo Jusbrasil'

export interface VariantBProps {
  title: string
  description: string
  searchParam: string
  buttonContent: React.ReactNode
  /** When provided, shows the "explore" section and invokes on click. */
  onExploreClick?: () => void
  /** Label for the explore button. If omitted, uses DEFAULT_EXPLORE_BUTTON_LABEL. */
  exploreButtonLabel?: string
  /** Experiment ID for explore section metrics. */
  experimentId?: string
  /** Value for metadata.page in sendMetric events. Default: 'not_found'. */
  page?: string
}

const EXPLORE_TEXT = 'Ou experimente explorar demais secções do Jusbrasil.'

export function VariantB({
  title,
  description,
  searchParam,
  buttonContent,
  onExploreClick,
  exploreButtonLabel = DEFAULT_EXPLORE_BUTTON_LABEL,
  experimentId,
  page,
}: VariantBProps) {
  const exploreButtonRef = useRef<HTMLButtonElement>(null)
  const exploreCtaViewSentRef = useRef(false)
  const session = useUserSession()
  const isExploreOnScreen = useOnScreen(exploreButtonRef, { threshold: 0.5 }, false)

  const exploreMetricsPayload = useMemo(
    () => ({
      app_id: 'web-search',
      metadata: {
        page: page ?? 'not_found',
        action: 'open_general_serp',
        cta_id: 'general_serp_search',
        destination: 'general_serp',
        copy: exploreButtonLabel,
        ...(experimentId != null && { experiment_id: experimentId }),
        variant: 'variantB',
        channel: 'web_entrypoint',
      },
    }),
    [exploreButtonLabel, experimentId, page]
  )

  useEffect(() => {
    if (onExploreClick && isExploreOnScreen && !exploreCtaViewSentRef.current) {
      sendMetrics('SeusDireitos.CtaView', session?.pid?.toString() ?? null, exploreMetricsPayload)
      exploreCtaViewSentRef.current = true
    }
  }, [onExploreClick, isExploreOnScreen, exploreMetricsPayload, session?.pid])

  const handleExploreClick = () => {
    sendMetrics('SeusDireitos.CtaClick', session?.pid?.toString() ?? null, exploreMetricsPayload)
    onExploreClick?.()
  }

  return (
    <Flex direction="column" gap="3xs" align="center">
      <Flex direction="column" align="center" gap="6xs" className={styles.textBlock}>
        <Heading size="md">{title}</Heading>
        <BodyText size="md">
          {description} <span className={styles.searchParam}>{searchParam}</span>
        </BodyText>
      </Flex>
      <Flex justify="center" className={styles.buttonContainer}>
        {buttonContent}
      </Flex>
      {onExploreClick != null && (
        <Flex direction="column" align="center" gap="3xs" className={styles.exploreSection}>
          <Separator orientation="horizontal" decorative />
          <BodyText size="md" className={styles.exploreText}>
            {EXPLORE_TEXT}
          </BodyText>
          <Flex justify="center" className={styles.exploreButtonContainer}>
            <Button
              ref={exploreButtonRef}
              kind="secondary"
              size="md"
              block
              onClick={handleExploreClick}
            >
              {exploreButtonLabel}
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
